import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'

/**
 * The gate state, exactly as the server computed it.
 *
 * **Nothing here re-derives a gate.** `docs/SCIENCE.md` §3.4 is implemented once,
 * in `model.ComputeGates` and `service.GateService`, and the client renders the
 * answer. A second implementation in TypeScript would be a second thing that can
 * be wrong, and the two would disagree on exactly the users who matter.
 *
 * SUAP-1202.
 */
export interface GateState {
  screened: boolean
  fasting_available: boolean
  /** `not_screened` | `blocked` | `needs_attestation` | '' */
  reason: string
  blocked: string[]
  awaiting_attestation: string[]
  attested: string[]
  version: number
}

/**
 * `loading` is a real state and not an absence — the same judgement
 * `AuthContext` makes about the session, and for a sharper reason: rendering the
 * fasting tab while the answer is in flight shows a blocked user the feature for
 * one paint, and rendering the blocked screen while it is in flight tells an
 * eligible user they are blocked. Neither is acceptable, so callers branch on
 * three states.
 */
export type GateStatus = 'loading' | 'ready' | 'unavailable'

interface GateValue {
  status: GateStatus
  state: GateState | null
  /** Re-reads after a submit, an attestation or a withdrawal. */
  refresh: () => Promise<void>

  /**
   * "My clinician approved this", for one gate.
   *
   * **The server decides whether it may be attested**, and refuses a hard block
   * with `gate_not_attestable` rather than ignoring it. This function does not
   * re-implement that rule — `SUAP-1202` puts it in `model.Gate.Blocking()` and
   * nowhere else, so a second copy here would be a second thing that can be
   * wrong. What the client does is not *offer* the action for a blocked gate;
   * that is presentation, and the refusal underneath it is the guarantee.
   */
  attest: (gate: string) => Promise<void>
  /** Withdraw a standing attestation. Fasting becomes unavailable again. */
  revoke: (gate: string) => Promise<void>
}

const GateContext = createContext<GateValue | null>(null)

/**
 * Fetches the gate state once per session and holds it.
 *
 * **`fastingAvailable` defaults to false while loading and on error.** A network
 * failure must not open a feature that §3.4 blocks — the client guard is a
 * courtesy to the person using the app, and the courtesy still has to fail in
 * the safe direction. The server refuses independently either way
 * (`middleware.RequireUngated`), which is what actually keeps a blocked user out.
 */
export function GateProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth()
  const [status, setStatus] = useState<GateStatus>('loading')
  const [state, setState] = useState<GateState | null>(null)

  const load = useCallback(async () => {
    try {
      const next = await api.get<GateState>('/api/health-screen/gates')
      setState(next)
      setStatus('ready')
    } catch {
      // Deliberately not distinguished. Whatever went wrong, the honest client
      // position is "we do not know", and "we do not know" must not render the
      // fasting feature.
      setState(null)
      setStatus('unavailable')
    }
  }, [])

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      setState(null)
      setStatus(authStatus === 'loading' ? 'loading' : 'unavailable')
      return
    }
    void load()
  }, [authStatus, load])

  // Both actions answer with the new gate state, so the screen never renders a
  // stale gate for a paint. The server returns it from every write for exactly
  // that reason (SUAP-1202's handler answers with `state()` on all four routes).
  const attest = useCallback(async (gate: string) => {
    setState(await api.post<GateState>('/api/health-screen/attestations', { gate }))
    setStatus('ready')
  }, [])

  const revoke = useCallback(async (gate: string) => {
    setState(await api.delete<GateState>(`/api/health-screen/attestations/${gate}`))
    setStatus('ready')
  }, [])

  const value = useMemo<GateValue>(
    () => ({ status, state, refresh: load, attest, revoke }),
    [status, state, load, attest, revoke],
  )
  return <GateContext.Provider value={value}>{children}</GateContext.Provider>
}

/** The gate state. Throws outside a provider, which is a wiring bug, not a state. */
export function useGates(): GateValue {
  const value = useContext(GateContext)
  if (value === null) throw new Error('useGates must be used inside a GateProvider')
  return value
}

/**
 * Whether the fasting feature may be rendered.
 *
 * One expression, in one place, so a surface added later cannot accidentally ask
 * a slightly different question.
 */
export function useFastingAvailable(): boolean {
  const { status, state } = useGates()
  return status === 'ready' && state?.fasting_available === true
}
